import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LightboxPreview, type ScanItem } from '../components/LightboxPreview';
import { buildRollSections, flattenScansForLightbox } from '../utils/rollUtils';
import { useFeature, SCAN_EDITING, PRINT_ORDERING } from '../contexts/ConfigContext';
import {
  usePrintSelection,
  PRINT_SIZES,
  DEFAULT_SIZE,
  type ScanMeta,
} from '../contexts/PrintSelectionContext';
import {
  usePrintPricing,
  discountPercent,
  estimatedTotal,
  unitPrice,
  gbp,
  tierRangeLabel,
} from '../hooks/usePrintPricing';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useConfirm } from '../hooks/useConfirm';
import type { Scan, RollSection, ScansResponse } from '../types/roll';
import styles from './GalleryPage.module.css';

/** A scan can be selected for print once its image is ready (not processing/failed). */
function isPrintable(scan: Scan): boolean {
  return scan.thumbnail_ready && scan.edit_status !== 'failed';
}

async function fetchScans(
  orderId: string,
  preview: boolean,
  handleUnauthorized: () => void
): Promise<ScansResponse> {
  // In preview mode an admin fetches the same payload via an admin-authorised
  // endpoint, so the rendered gallery is identical to the customer's.
  const url = preview
    ? `/api/v1/admin/orders/${orderId}/preview-scans`
    : `/api/v1/orders/${orderId}/scans`;
  const res = await fetch(url, { credentials: 'include' });
  // Only preview mode is behind the admin session; the customer path has its own
  // auth context and must not clear the admin flag on its 401s.
  if (preview && (res.status === 401 || res.status === 403)) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch scans (${res.status})`);
  }
  return res.json();
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Number of images in the first row that get eager loading (desktop 4-column grid). */
const EAGER_LOAD_COUNT = 4;

/** Poll interval while any scan (e.g. a rendering edit) is still processing. */
const PROCESSING_POLL_MS = 3000;

/** True while a scan is still being generated (and hasn't failed). */
function isProcessing(scan: Scan): boolean {
  return !scan.thumbnail_ready && scan.edit_status !== 'failed';
}

export function GalleryPage({ preview = false }: { preview?: boolean } = {}) {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editingEnabled = useFeature(SCAN_EDITING) && !preview;
  const printEnabled = useFeature(PRINT_ORDERING) && !preview;
  const printSelection = usePrintSelection();
  const { handleUnauthorized } = useAdminAuth();
  const { confirm, confirmDialog } = useConfirm();
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deletingScanIds, setDeletingScanIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [contactSheetRollId, setContactSheetRollId] = useState<string | null>(null);
  const [contactSheetError, setContactSheetError] = useState<{ rollId: string; message: string } | null>(null);
  // Print selection is opt-in: the checkboxes and roll controls stay hidden so
  // images can be viewed unobstructed until the customer taps "Order prints".
  // "?print=1" opens it straight away — the scans-ready and expiry emails link
  // that way so their print CTA lands on something actionable rather than
  // making the customer find the button themselves.
  const [searchParams] = useSearchParams();
  const [printMode, setPrintMode] = useState(() => searchParams.get('print') === '1');
  const autoOpenedPrintMode = useRef(false);

  // Pricing drives the running total and the volume-discount table; only fetch
  // it once the customer opens print mode.
  const { data: pricing } = usePrintPricing(printEnabled && printMode);

  const { data, isLoading, isError, error } = useQuery<ScansResponse>({
    // Keep the preview payload cache-separate from the customer one.
    queryKey: ['scans', orderId, preview],
    queryFn: () => fetchScans(orderId!, preview, handleUnauthorized),
    enabled: !!orderId,
    // While anything is still rendering (a saved edit, a fresh thumbnail),
    // poll until every scan is ready or failed.
    refetchInterval: (query) => {
      const rolls = query.state.data?.rolls;
      const anyProcessing = rolls?.some((roll) => roll.scans.some(isProcessing));
      return anyProcessing ? PROCESSING_POLL_MS : false;
    },
  });

  usePageTitle(preview ? 'Admin — preview' : data?.order_number ?? 'Gallery');

  const handleEditScan = useCallback(
    (scan: ScanItem) => {
      setLightboxIndex(null);
      navigate(`/orders/${orderId}/edit/${scan.scan_id}`);
    },
    [navigate, orderId],
  );

  const handleDeleteEdit = useCallback(
    async (scan: Scan) => {
      const confirmed = await confirm({
        title: 'Delete edit',
        message:
          'Delete this edited image? This cannot be undone — the original scan is unaffected.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!confirmed) return;
      setDeleteError(null);
      setDeletingScanIds((prev) => new Set(prev).add(scan.scan_id));
      try {
        const res = await fetch(`/api/v1/edits/${orderId}/${scan.scan_id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!res.ok && res.status !== 404) {
          throw new Error(`Delete failed (${res.status})`);
        }
        await queryClient.invalidateQueries({ queryKey: ['scans', orderId] });
      } catch {
        setDeleteError('Could not delete the edit. Please try again.');
      } finally {
        setDeletingScanIds((prev) => {
          const next = new Set(prev);
          next.delete(scan.scan_id);
          return next;
        });
      }
    },
    [orderId, queryClient, confirm],
  );

  const handleContactSheet = useCallback(
    async (rollId: string) => {
      setContactSheetError(null);
      setContactSheetRollId(rollId);
      try {
        const res = await fetch(
          `/api/v1/orders/${orderId}/rolls/${rollId}/contact-sheet`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          let message = 'Could not generate the contact sheet. Please try again.';
          try {
            const body = await res.json();
            if (body?.error) message = body.error;
          } catch {
            // keep the default message
          }
          setContactSheetError({ rollId, message });
          return;
        }
        const { url, filename } = await res.json();
        // The presigned URL carries a Content-Disposition attachment header, so
        // a plain anchor click downloads the PDF without navigating away.
        const link = document.createElement('a');
        link.href = url;
        if (filename) link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        setContactSheetError({
          rollId,
          message: 'Could not generate the contact sheet. Please try again.',
        });
      } finally {
        setContactSheetRollId((current) => (current === rollId ? null : current));
      }
    },
    [orderId],
  );

  // In preview, an admin returns to the order's admin page; otherwise the
  // customer returns to their order list.
  const backTo = preview ? `/admin/orders/${orderId}/upload` : '/orders';
  const backLabel = preview ? '← Back to order' : '← Back to orders';

  const handleImageError = useCallback((scanId: string) => {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(scanId);
      return next;
    });
  }, []);

  const rollSections = useMemo(() => {
    if (!data?.rolls) return [];
    return buildRollSections(data.rolls);
  }, [data?.rolls]);

  const flatScans = useMemo(() => {
    if (!data?.rolls) return [];
    return flattenScansForLightbox(data.rolls);
  }, [data?.rolls]);

  /** Build print ScanMeta for one printable scan within a section. */
  const scanMeta = useCallback(
    (scan: Scan, rollId: string): ScanMeta => ({
      scan_id: scan.scan_id,
      filename: scan.original_filename,
      thumbnail_url: scan.thumbnail_url,
      roll_id: rollId,
      // Lets the basket offer a way back to this gallery. Carried on the line
      // itself rather than in router state, which is lost the moment the page
      // reloads and absent entirely when /print is reached from the header nav.
      order_id: orderId,
    }),
    [orderId],
  );

  // Enrich any selection restored from the server (which persists only
  // scan_id/size/finish/quantity) with fresh filenames + thumbnail URLs.
  const allScanMetas = useMemo(
    () =>
      rollSections.flatMap((section) =>
        section.scans.filter(isPrintable).map((s) => scanMeta(s, section.roll_id)),
      ),
    [rollSections, scanMeta],
  );
  useEffect(() => {
    if (printEnabled && allScanMetas.length > 0) {
      printSelection.hydrateFromScans(allScanMetas);
    }
    // printSelection identity changes each render; depend only on the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allScanMetas, printEnabled]);

  // Reopen print mode automatically when a persisted selection loads, so a
  // returning customer sees their in-progress order without hunting for the
  // button. Runs once; the customer can still close it manually afterwards.
  const hasSelection = printSelection.lines.length > 0;
  useEffect(() => {
    if (printEnabled && !autoOpenedPrintMode.current && hasSelection) {
      autoOpenedPrintMode.current = true;
      setPrintMode(true);
    }
  }, [printEnabled, hasSelection]);

  /** Printable scans in a section, and whether all are currently selected. */
  const rollPrintState = useCallback(
    (section: RollSection) => {
      const metas = section.scans.filter(isPrintable).map((s) => scanMeta(s, section.roll_id));
      const allSelected =
        metas.length > 0 && metas.every((m) => printSelection.isScanSelected(m.scan_id));
      return { metas, allSelected };
    },
    [scanMeta, printSelection],
  );

  const displayOrderName = data?.order_number ?? `Order ${orderId}`;

  /**
   * Opens the lightbox at the correct flat index given a roll section index
   * and a scan index within that section.
   */
  const openLightbox = useCallback(
    (sectionIndex: number, scanIndexInSection: number) => {
      // Calculate the flat index by summing scans in all preceding sections
      let flatIndex = 0;
      for (let i = 0; i < sectionIndex; i++) {
        flatIndex += rollSections[i].scans.length;
      }
      flatIndex += scanIndexInSection;
      setLightboxIndex(flatIndex);
    },
    [rollSections]
  );

  if (isLoading) {
    return (
      <main className={styles.page}>
        <Link to={backTo} className={styles.backLink}>
          {backLabel}
        </Link>
        <h1 className={styles.heading}>{displayOrderName}</h1>
        <p className={styles.loading}>Loading scans...</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className={styles.page}>
        <Link to={backTo} className={styles.backLink}>
          {backLabel}
        </Link>
        <h1 className={styles.heading}>{displayOrderName}</h1>
        <div className={styles.fetchError} role="alert">
          <p>{error instanceof Error ? error.message : 'Something went wrong.'}</p>
        </div>
      </main>
    );
  }

  // Count originals only, so the gallery total matches the order list's stable
  // count. `data.scan_count` reflects all current scans including the customer's
  // "My edits" copies, which inflates the figure as edits are saved; the edits
  // roll carries its own count in its header instead.
  const scanCount = rollSections
    .filter((section) => section.roll_type !== 'edits')
    .reduce((sum, section) => sum + section.scans.length, 0);

  if (data?.expired) {
    return (
      <main className={styles.page}>
        <Link to={backTo} className={styles.backLink}>
          {backLabel}
        </Link>
        <h1 className={styles.heading}>{displayOrderName}</h1>
        <div className={styles.expiredMessage} role="alert">
          <p>
            The scans for this order are no longer available.
            {data.expired_at && (
              <> The retention period expired on {formatDate(data.expired_at)}.</>
            )}
          </p>
        </div>
      </main>
    );
  }

  // Print pricing for the running total + discount table (only meaningful once
  // print mode is open and pricing has loaded).
  const hasPrintableScans = allScanMetas.length > 0;
  // Whether the per-scan selection controls (checkboxes, roll buttons) show.
  const printSelecting = printEnabled && printMode;
  // Cross-roll "select all" state for this order's printable scans.
  const selectedPrintCount = allScanMetas.filter((m) =>
    printSelection.isScanSelected(m.scan_id),
  ).length;
  const allPrintSelected = hasPrintableScans && selectedPrintCount === allScanMetas.length;
  const discountTiers = pricing?.volume_discounts ?? [];
  // Sizes to show a per-print price column for (only those we have a price for).
  const pricedSizes = PRINT_SIZES.filter((s) => pricing?.prices?.[s]?.gbp != null);
  const activePct = discountPercent(printSelection.totalQuantity, discountTiers);
  // Sizes/finishes are picked at checkout, so the bar quotes an all-4×6 order
  // (the default size). Price every print at the base size for a total that
  // always matches its "as 4×6 prints" label.
  const baseSizeLabel = DEFAULT_SIZE.replace('x', '×');
  const runningTotal = estimatedTotal(
    printSelection.lines.map((l) => ({ size: DEFAULT_SIZE, quantity: l.quantity })),
    pricing,
    activePct,
  );

  return (
    <main className={styles.page}>
      <Link to={backTo} className={styles.backLink}>
          {backLabel}
        </Link>
      <h1 className={styles.heading}>{displayOrderName}</h1>
      <p className={styles.scanCount}>
        {scanCount} {scanCount === 1 ? 'scan' : 'scans'}
      </p>

      {preview && (
        <div className={styles.previewBanner} role="status">
          <strong>Preview as customer.</strong> This is exactly what the customer sees.
          Download and other actions are disabled here.
        </div>
      )}

      {flatScans.length > 0 && (
        <p className={styles.intro}>
          Here are the scans from your order, grouped by roll below.
          {editingEnabled ? (
            <>
              {' '}
              Tap any scan to view it full-screen, then choose{' '}
              <strong>Edit photo</strong> to crop, add a border, or adjust
              exposure, contrast and colour. Your edited copies are saved to a
              new <strong>My edits</strong> roll below, ready to download or
              print. Use the download button to save any images to your device.
            </>
          ) : (
            " Click the download button to choose which images you'd like to save."
          )}
        </p>
      )}

      {flatScans.length > 0 && (
        <div className={styles.actions}>
          {preview ? (
            <span className={styles.downloadDisabled} aria-disabled="true">
              Download scans (disabled in preview)
            </span>
          ) : (
            <Link to={`/orders/${orderId}/download`} className={styles.downloadLink}>
              Download scans
            </Link>
          )}
          {printEnabled && hasPrintableScans && (
            <button
              type="button"
              className={`${styles.orderPrintsButton}${printMode ? ` ${styles.orderPrintsButtonActive}` : ''}`}
              onClick={() => setPrintMode((on) => !on)}
              aria-pressed={printMode}
            >
              {printMode ? 'Done ordering prints' : 'Order prints'}
            </button>
          )}
        </div>
      )}

      {printEnabled && printMode && (
        <div className={styles.printIntro}>
          <p className={styles.printIntroText}>
            Tick the images you'd like as prints, then review your order. Sizes
            and finishes are chosen at the next step.
          </p>
          {discountTiers.length > 0 && (
            <table className={styles.discountTable}>
              <caption className={styles.discountCaption}>
                Price per print — volume discounts applied automatically
              </caption>
              <thead>
                <tr>
                  <th scope="col">Prints</th>
                  {pricedSizes.map((s) => (
                    <th key={s} scope="col" className={styles.discountPriceCol}>
                      {s.replace('x', '×')}
                    </th>
                  ))}
                  <th scope="col" className={styles.discountPriceCol}>
                    Discount
                  </th>
                </tr>
              </thead>
              <tbody>
                {discountTiers.map((tier) => {
                  const isActive =
                    printSelection.totalQuantity >= tier.min &&
                    (tier.max == null || printSelection.totalQuantity <= tier.max);
                  return (
                    <tr
                      key={`${tier.min}-${tier.max ?? 'plus'}`}
                      className={isActive ? styles.discountRowActive : undefined}
                    >
                      <td>{tierRangeLabel(tier)}</td>
                      {pricedSizes.map((s) => {
                        const u = unitPrice(s, pricing, tier.percent);
                        return (
                          <td key={s} className={styles.discountPriceCol}>
                            {u != null ? gbp(u) : '—'}
                          </td>
                        );
                      })}
                      <td className={styles.discountPriceCol}>
                        {tier.percent > 0 ? `${tier.percent}% off` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {printSelecting && hasPrintableScans && (
        <div className={styles.printToolbar}>
          <label className={styles.selectAllLabel}>
            <input
              type="checkbox"
              className={styles.selectAllCheckbox}
              checked={allPrintSelected}
              onChange={() =>
                allPrintSelected
                  ? printSelection.deselectScans(allScanMetas.map((m) => m.scan_id))
                  : printSelection.selectRoll(allScanMetas)
              }
              aria-label="Select all photos for print"
            />
            Select all
          </label>
          {selectedPrintCount > 0 && (
            <span className={styles.selectionHint}>
              {selectedPrintCount} of {allScanMetas.length} selected
            </span>
          )}
        </div>
      )}

      {rollSections.length === 0 ? (
        <p className={styles.emptyState}>No scans available for this order</p>
      ) : (
        rollSections.map((section, sectionIndex) => {
          const isEditsRoll = section.roll_type === 'edits';
          const generatingSheet = contactSheetRollId === section.roll_id;
          const sheetError =
            contactSheetError?.rollId === section.roll_id
              ? contactSheetError.message
              : null;
          return (
            <section key={section.roll_id} className={styles.rollSection}>
              <div className={styles.rollHeader}>
                <h2 className={styles.rollHeading}>
                  {section.roll_name}
                  {isEditsRoll && ` · ${section.scans.length}`}
                </h2>
                <div className={styles.rollActions}>
                  {printSelecting && (() => {
                    const { metas, allSelected } = rollPrintState(section);
                    if (metas.length === 0) return null;
                    return (
                      <label className={styles.rollCheckboxLabel}>
                        <input
                          type="checkbox"
                          className={styles.rollCheckbox}
                          checked={allSelected}
                          onChange={() =>
                            allSelected
                              ? printSelection.deselectScans(metas.map((m) => m.scan_id))
                              : printSelection.selectRoll(metas)
                          }
                          aria-label={`Select all scans in ${section.roll_name} for print`}
                        />
                        Select all
                      </label>
                    );
                  })()}
                  {!preview && section.scans.length > 0 && (
                    <button
                      type="button"
                      className={styles.contactSheetButton}
                      onClick={() => handleContactSheet(section.roll_id)}
                      disabled={generatingSheet}
                      aria-label={`Download a contact sheet PDF for ${section.roll_name}`}
                    >
                      {generatingSheet ? 'Generating…' : 'Contact sheet'}
                    </button>
                  )}
                </div>
              </div>
              {sheetError && (
                <p className={styles.contactSheetError} role="alert">
                  {sheetError}
                </p>
              )}

              {section.scans.length === 0 ? (
                <p className={styles.rollEmptyState}>No scans available in this roll</p>
              ) : (
                <div className={styles.grid}>
                  {section.scans.map((scan, scanIndex) => {
                    const globalIndex = flatScans.findIndex(
                      (s) => s.scan_id === scan.scan_id
                    );
                    const deletable = isEditsRoll && editingEnabled;
                    const deleting = deletingScanIds.has(scan.scan_id);

                    let tileContent;
                    if (scan.edit_status === 'failed') {
                      tileContent = (
                        <div className={styles.errorPlaceholder}>Edit failed</div>
                      );
                    } else if (!scan.thumbnail_ready) {
                      tileContent = (
                        <div
                          className={styles.processingPlaceholder}
                          role="status"
                          aria-label={`Processing ${scan.original_filename}`}
                        >
                          Processing…
                        </div>
                      );
                    } else if (failedImages.has(scan.scan_id)) {
                      tileContent = (
                        <div className={styles.errorPlaceholder}>Failed to load</div>
                      );
                    } else {
                      tileContent = (
                        <img
                          src={scan.thumbnail_url}
                          alt={scan.original_filename}
                          className={styles.thumbnailImage}
                          loading={globalIndex < EAGER_LOAD_COUNT ? 'eager' : 'lazy'}
                          onError={() => handleImageError(scan.scan_id)}
                        />
                      );
                    }

                    const printable = isPrintable(scan);
                    // In print mode a printable tile becomes a click-to-select
                    // card (mirroring the download flow); a magnifier button
                    // still opens the full-screen lightbox.
                    const printSelectable = printSelecting && printable;
                    const selectedForPrint =
                      printSelectable && printSelection.isScanSelected(scan.scan_id);
                    const toggleForPrint = () =>
                      printSelection.toggleScan(scanMeta(scan, section.roll_id));

                    return (
                      <div key={scan.scan_id} className={styles.tile}>
                        {printSelectable ? (
                          <div
                            className={`${styles.selectCard}${selectedForPrint ? ` ${styles.selectCardSelected}` : ''}`}
                            role="checkbox"
                            aria-checked={!!selectedForPrint}
                            aria-label={`Select ${scan.original_filename} for print`}
                            tabIndex={0}
                            onClick={toggleForPrint}
                            onKeyDown={(e) => {
                              if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                toggleForPrint();
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              className={styles.selectCheckbox}
                              checked={!!selectedForPrint}
                              readOnly
                              aria-hidden="true"
                              tabIndex={-1}
                            />
                            {tileContent}
                            <button
                              type="button"
                              className={styles.viewButton}
                              aria-label={`View ${scan.original_filename} full screen`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openLightbox(sectionIndex, scanIndex);
                              }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                aria-hidden="true"
                              >
                                <circle cx="11" cy="11" r="7" />
                                <line x1="21" y1="21" x2="16.5" y2="16.5" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className={styles.thumbnailButton}
                            data-scan-index={globalIndex}
                            aria-label={`View scan ${scan.original_filename}`}
                            onClick={() => openLightbox(sectionIndex, scanIndex)}
                            disabled={isProcessing(scan)}
                          >
                            {tileContent}
                          </button>
                        )}
                        {deletable && (
                          <button
                            type="button"
                            className={styles.deleteEditButton}
                            aria-label={`Delete edit ${scan.original_filename}`}
                            onClick={() => handleDeleteEdit(scan)}
                            disabled={deleting}
                          >
                            {deleting ? '…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })
      )}

      {deleteError && (
        <p className={styles.fetchError} role="alert">
          {deleteError}
        </p>
      )}

      {lightboxIndex !== null && (
        <LightboxPreview
          scans={flatScans}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onEdit={editingEnabled ? handleEditScan : undefined}
        />
      )}

      {printSelecting && printSelection.lines.length > 0 && (
        <div className={styles.printBar} role="status" aria-live="polite">
          <div className={styles.printBarInfo}>
            <span className={styles.printBarCount}>
              {printSelection.lines.length}{' '}
              {printSelection.lines.length === 1 ? 'image' : 'images'} selected
              {' · '}
              {printSelection.totalQuantity}{' '}
              {printSelection.totalQuantity === 1 ? 'print' : 'prints'}
            </span>
            {runningTotal != null && (
              <>
                <span className={styles.printBarTotal}>
                  {gbp(runningTotal)}
                  {activePct > 0 && (
                    <span className={styles.printBarDiscount}>
                      {' '}
                      ({activePct}% off)
                    </span>
                  )}
                </span>
                <span className={styles.printBarNote}>
                  Estimated as {baseSizeLabel} prints — choose sizes at checkout
                </span>
              </>
            )}
          </div>
          {/*
            Carry the gallery we came from so the basket can offer a way back to
            this exact roll rather than the order list. Router state does not
            survive a full page load (returning from Shopify checkout, or a
            bookmarked /print), so the basket treats it as a nicety and falls
            back to the order list.
          */}
          <Link
            to="/print"
            state={{ fromGallery: `/orders/${orderId}` }}
            className={styles.printBarLink}
          >
            Review print order →
          </Link>
        </div>
      )}

      {confirmDialog}
    </main>
  );
}
